import type { IconName } from '../components/AppIcon';

/**
 * The three browser permissions the app depends on. Kept structurally identical
 * to `PermName` in `hooks/usePermission.ts` so the two interop without casting.
 */
export type PermissionName = 'geolocation' | 'camera' | 'notifications';

export interface PermissionMeta {
  /** Lucide icon name (rendered via AppIcon). */
  icon: IconName;
  /** i18n key for the permission's short title. */
  titleKey: string;
  /** i18n key for the one-line "why we need this" description. */
  descKey: string;
}

/**
 * Presentational metadata for each permission. Titles/descriptions are i18n keys
 * (the strings already live in the locale files) so callers just `t(meta.titleKey)`.
 */
export const PERMISSION_META: Record<PermissionName, PermissionMeta> = {
  geolocation: {
    icon: 'MapPin',
    titleKey: 'Location',
    descKey: "Used to confirm you're at your workplace when you clock in.",
  },
  camera: {
    icon: 'Camera',
    titleKey: 'Camera',
    descKey: 'Used to capture your attendance photo and face recognition.',
  },
  notifications: {
    icon: 'Bell',
    titleKey: 'Notifications',
    descKey: 'Get notified about approvals, shift swaps and clock reminders.',
  },
};

/**
 * Ask the browser for a one-shot geolocation fix. Resolves `true` when a position
 * comes back (the permission is effectively usable), `false` on denial/timeout or
 * when geolocation is unsupported. Never rejects — callers branch on the boolean.
 *
 * Note: on a hard-denied permission the browser resolves the error path WITHOUT a
 * prompt; there is no web API to re-open the prompt. Recovery is the site-settings
 * control in the address bar (surface a hint, not a silent no-op button).
 */
export function requestGeolocation(): Promise<boolean> {
  return new Promise((resolve) => {
    if (!('geolocation' in navigator) || !navigator.geolocation?.getCurrentPosition) {
      resolve(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      () => resolve(true),
      () => resolve(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  });
}

/**
 * Trigger the browser's camera prompt via getUserMedia, then immediately release
 * the stream (we only wanted the grant, not to hold the camera open). Resolves
 * `true` when access is granted, `false` on denial or when unsupported.
 */
export async function requestCamera(): Promise<boolean> {
  if (!navigator.mediaDevices?.getUserMedia) return false;
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
    stream.getTracks().forEach((track) => track.stop());
    return true;
  } catch {
    return false;
  }
}
